import { useRef, useState } from "react";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { beatMax } from "../beatUtils";
import { Briefing } from "./Shared";
import { Crosshair, CheckCircle2 } from "lucide-react";

// Drag the centring cross + collimation box onto the correct landmark.
export function PositioningBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const target = beat.target || { x: 50, y: 50 };
  const tol = beat.tolerance || { full: 9, half: 20 };
  const boxPct = beat.box ?? 46;
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [moved, setMoved] = useState(false);
  const [res, setRes] = useState(null);
  const [drag, setDrag] = useState(false);
  const checked = !!res;

  const setFrom = (clientX, clientY) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const x = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - r.top) / r.height) * 100));
    setPos({ x, y });
    setMoved(true);
  };
  const onDown = (e) => {
    if (checked) return;
    // Capture the pointer so drags don't drop when the finger leaves the canvas
    // edge on touch devices (fixes Radiography drag on mobile).
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    setDrag(true);
    setFrom(e.clientX, e.clientY);
  };
  const onMove = (e) => { if (drag && !checked) setFrom(e.clientX, e.clientY); };
  const stop = (e) => {
    setDrag(false);
    if (e && e.currentTarget && e.pointerId !== undefined) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
  };

  const dist = Math.hypot(pos.x - target.x, pos.y - target.y);

  const onPrimary = () => {
    if (!checked) {
      const earned = dist <= tol.full ? (beat.full ?? 20) : dist <= tol.half ? Math.round((beat.full ?? 20) / 2) : 0;
      setRes({ earned, max: beatMax(beat), detail: `${dist <= tol.full ? "Centred accurately" : dist <= tol.half ? "Close — slightly off" : "Off-centre"} on ${beat.landmark}`, key: beat.key, label: beat.label, hit: dist <= tol.full });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Crosshair}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : moved ? "Lock the centring point" : "Drag the cross to centre"}
      onPrimary={onPrimary}
      primaryDisabled={!moved && !checked}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      <div
        ref={ref}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={stop}
        onPointerCancel={stop}
        data-testid="positioning-canvas"
        className="relative rounded-lg overflow-hidden border-2 border-slate-700 bg-slate-900 touch-none select-none cursor-crosshair"
        style={{ aspectRatio: "16 / 10" }}
      >
        {/* dog lateral thorax silhouette */}
        <svg viewBox="0 0 320 200" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          <path d="M30 150 Q40 90 90 80 Q120 60 160 64 L210 66 Q250 64 280 92 Q296 110 286 140 L270 142 Q258 120 240 124 L120 124 Q70 124 56 152 Z" fill="#334155" opacity="0.9" />
          {/* ribs hint */}
          {[0, 1, 2, 3, 4].map((i) => (
            <path key={i} d={`M${120 + i * 26} 84 Q${112 + i * 26} 110 ${136 + i * 26} 122`} stroke="#475569" strokeWidth="3" fill="none" />
          ))}
          {/* scapula landmark hint */}
          <line x1="118" y1="74" x2="118" y2="124" stroke="#64748b" strokeWidth="2" strokeDasharray="3 3" />
        </svg>

        {/* collimation box + cross */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${pos.x}%`, top: `${pos.y}%` }}>
          <div className="relative border-2 border-amber-300/80 border-dashed" style={{ width: `${boxPct * 3}px`, height: `${boxPct * 2}px` }}>
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
              <span className="absolute w-6 h-[2px] bg-amber-300" />
              <span className="absolute h-6 w-[2px] bg-amber-300" />
            </span>
          </div>
        </div>

        {/* target marker (on reveal) */}
        {checked && (
          <div className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-emerald-400 bg-emerald-400/30" style={{ left: `${target.x}%`, top: `${target.y}%` }} data-testid="positioning-target" />
        )}
      </div>
      <p className="text-[11px] text-slate-500">Drag anywhere on the canvas to place the centring cross and collimation box over <span className="font-semibold">{beat.landmark}</span>.</p>

      {checked && (
        <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${res.hit ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`${beat.key}-note`}>
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{beat.coach}</span>
        </div>
      )}
    </BeatShell>
  );
}
